import { useCallback, useEffect, useRef, useState, type SetStateAction } from "react";
import {
  publishFeedback,
  subscribeFeedback,
  type FeedbackNotice,
  type FeedbackTone,
} from "@taunggyi-plus/api-client";

/** Preserve inline feedback while announcing each action, including repeated results. */
export function useFeedback(initial = "", tone: FeedbackTone = "info") {
  const [message, setMessage] = useState(initial);
  const current = useRef(initial);
  const update = useCallback(
    (value: SetStateAction<string>) => {
      const next = typeof value === "function" ? value(current.current) : value;
      current.current = next;
      setMessage(next);
      if (next) publishFeedback(next, tone);
    },
    [tone],
  );
  return [message, update] as const;
}

function Toast({ notice, dismiss }: { notice: FeedbackNotice; dismiss: (id: number) => void }) {
  const [paused, setPaused] = useState(false);
  useEffect(() => {
    if (paused || notice.tone === "error") return;
    const timer = window.setTimeout(() => dismiss(notice.id), 7000);
    return () => window.clearTimeout(timer);
  }, [dismiss, notice.id, notice.tone, paused]);
  return (
    <div
      className={`feedback-toast feedback-toast--${notice.tone}`}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setPaused(false);
      }}
    >
      <span className="feedback-toast__icon" aria-hidden="true">
        {notice.tone === "error" ? "!" : "i"}
      </span>
      <div
        className="feedback-toast__content"
        role={notice.tone === "error" ? "alert" : "status"}
        aria-atomic="true"
      >
        <strong>{notice.tone === "error" ? "Something went wrong" : "Update"}</strong>
        <p>{notice.message}</p>
      </div>
      <button
        type="button"
        className="feedback-toast__close"
        aria-label="Dismiss message"
        onClick={() => dismiss(notice.id)}
      >
        ×
      </button>
    </div>
  );
}

export function FeedbackToasts() {
  const [notices, setNotices] = useState<FeedbackNotice[]>([]);
  useEffect(
    () =>
      subscribeFeedback((notice) => {
        setNotices((current) => {
          if (
            notice.tone === "error" &&
            current.some((item) => item.message === notice.message && item.tone === "error")
          )
            return current;
          return [...current.filter((item) => item.message !== notice.message), notice].slice(-4);
        });
      }),
    [],
  );
  const dismiss = useCallback(
    (id: number) => setNotices((current) => current.filter((item) => item.id !== id)),
    [],
  );
  return (
    <aside className="feedback-toasts" aria-label="Action messages">
      {notices.map((notice) => (
        <Toast key={notice.id} notice={notice} dismiss={dismiss} />
      ))}
    </aside>
  );
}
